/**
 * Platform Settings — Super Admin-specific settings experience.
 * Full 8-section layout: Platform Profile, Authentication & Sessions,
 * Email Delivery, Onboarding Defaults, Public Forms & Anti-Spam,
 * Feature Catalogue, Data & Retention, System Information.
 * Password-changing controls removed for passwordless product.
 * Read-only display — edit via server environment.
 */
import React, { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  KeyIcon,
  FolderOpenIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { getRoles, getHealthStatus } from '../features/super-admin/api/superAdminApi';
import { VERSION_INFO } from '../contexts/AuthContext';
import { DEFAULT_FEATURES, DEFAULT_PAGES, type RoleDefinition, type HealthStatus, type FeatureDefinition, type PageDefinition } from '../features/super-admin/types';

const PlatformSettingsPage: React.FC = () => {
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [health, setHealth] = useState<HealthStatus | null>(null);

  useEffect(() => {
    getRoles().then((data) => setRoles(data.roles || [])).catch(() => {});
    getHealthStatus().then((data) => setHealth(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Platform Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Platform-wide configuration and system information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 1. Platform Profile */}
        <SettingsSection
          icon={<Cog6ToothIcon className="w-4 h-4" />}
          title="Platform Profile"
          className="lg:col-span-2"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SettingsRow label="Platform Name" value="eSanza" />
            <SettingsRow label="Company" value="eSanza Ltd" />
            <SettingsRow label="Support Email" value="info@esanza.com" />
            <SettingsRow label="Website" value="esanza.com" />
            <SettingsRow label="Country" value="Rwanda" />
            <SettingsRow label="Currency" value="RWF (FR)" />
          </div>
        </SettingsSection>

        {/* 2. Authentication & Sessions */}
        <SettingsSection
          icon={<KeyIcon className="w-4 h-4" />}
          title="Authentication & Sessions"
        >
          <SettingsRow label="Authentication" value="Passwordless (OTP)" />
          <SettingsRow label="OTP Delivery" value="Email" />
          <SettingsRow label="Session Duration" value="24 hours" />
          <SettingsRow label="Multi-Tenant" value="Workspace selection on login" />
          <SettingsRow label="Password Policy" value="N/A — passwordless product" />
          <SettingsRow label="Login Throttling" value="Configured server-side" />
        </SettingsSection>

        {/* 3. Email Delivery */}
        <SettingsSection
          icon={<EnvelopeIcon className="w-4 h-4" />}
          title="Email Delivery"
        >
          <SettingsRow label="OTP Channel" value="Email" />
          <SettingsRow label="Approval Notifications" value="Email" />
          <SettingsRow label="Provider" value="Configured via environment" />
          <SettingsRow label="Sender Name" value="eSanza" />
          <SettingsRow label="Status" value="Check server environment for details" />
          <div className="px-4 py-2">
            <p className="text-xs text-slate-400">Email provider credentials are managed via server environment variables. Never exposed in this interface.</p>
          </div>
        </SettingsSection>

        {/* 4. Onboarding Defaults */}
        <SettingsSection
          icon={<UserGroupIcon className="w-4 h-4" />}
          title="Onboarding Defaults"
        >
          <SettingsRow label="Approval Policy" value="Manual review by Super Admin" />
          <SettingsRow label="Default Role" value="Project Manager" />
          <SettingsRow label="Default Modules" value={`${DEFAULT_FEATURES.filter(f => f.enabled).length} enabled by default`} />
          <SettingsRow label="Default Pages" value={`${DEFAULT_PAGES.filter(p => p.enabled).length} enabled by default`} />
          <SettingsRow label="Notification Recipients" value="Platform Owner" />
          <div className="px-4 py-2">
            <p className="text-xs text-slate-400">New tenants receive default feature/page access. Customise per-organisation via Organisation Management.</p>
          </div>
        </SettingsSection>

        {/* 5. Public Forms & Anti-Spam */}
        <SettingsSection
          icon={<ExclamationTriangleIcon className="w-4 h-4" />}
          title="Public Forms & Anti-Spam"
        >
          <SettingsRow label="Registration Form" value="Active" />
          <SettingsRow label="Try eSanza Form" value="Active" />
          <SettingsRow label="Contact Form" value="Active" />
          <SettingsRow label="Rate Limiting" value="Configured server-side" />
          <SettingsRow label="Turnstile (CAPTCHA)" value="Configured via environment" />
          <SettingsRow label="Spam Threshold" value="Server-managed" />
          <div className="px-4 py-2">
            <p className="text-xs text-slate-400">Turnstile secret keys and anti-spam thresholds are managed server-side. Never exposed in this interface.</p>
          </div>
        </SettingsSection>

        {/* 6. Feature Catalogue */}
        <SettingsSection
          icon={<ShieldCheckIcon className="w-4 h-4" />}
          title="Feature Catalogue"
          className="lg:col-span-2"
        >
          <p className="px-4 py-2 text-xs text-slate-500 mb-2">
            Platform feature and page definitions. Edit per-organisation access via Organisation Management → Access.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pb-4">
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Feature Modules ({DEFAULT_FEATURES.length})</h4>
              <div className="space-y-1">
                {DEFAULT_FEATURES.map((f) => (
                  <div key={f.key} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg text-sm">
                    <span className="text-slate-700">{f.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">{f.category}</span>
                      <span className={`w-2 h-2 rounded-full ${f.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Dashboard Pages ({DEFAULT_PAGES.length})</h4>
              <div className="space-y-1">
                {DEFAULT_PAGES.map((p) => (
                  <div key={p.key} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg text-sm">
                    <span className="text-slate-700">{p.title}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">#{p.order}</span>
                      <span className={`w-2 h-2 rounded-full ${p.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SettingsSection>

        {/* 7. Data & Retention */}
        <SettingsSection
          icon={<FolderOpenIcon className="w-4 h-4" />}
          title="Data & Retention"
        >
          <SettingsRow label="Backup" value="PostgreSQL managed by hosting provider" />
          <SettingsRow label="Audit Logs" value="Retained indefinitely" />
          <SettingsRow label="Soft Delete" value="Users and organisations archived, not hard-deleted" />
          <SettingsRow label="Export Policy" value="Admin-controlled data export" />
          <SettingsRow label="Deletion Behaviour" value="Soft delete with audit trail" />
          <div className="px-4 py-2">
            <p className="text-xs text-slate-400">Database operations are managed server-side. Never execute raw operations from the browser.</p>
          </div>
        </SettingsSection>

        {/* 8. System Information */}
        <SettingsSection
          icon={<ServerIcon className="w-4 h-4" />}
          title="System Information"
        >
          <SettingsRow label="App Name" value={VERSION_INFO.app_name || 'eSanza'} />
          <SettingsRow label="Backend Version" value={VERSION_INFO.backend_version || 'unknown'} />
          <SettingsRow label="Frontend Version" value={VERSION_INFO.frontend_version || 'unknown'} />
          <SettingsRow label="Git Commit" value={VERSION_INFO.git_commit || 'unknown'} />
          <SettingsRow label="Environment" value={VERSION_INFO.environment || 'development'} />
          <SettingsRow label="API Health" value={health?.status === 'healthy' ? 'Healthy' : 'Unhealthy'} />
          <SettingsRow label="Database" value={health?.database === 'connected' ? 'Connected' : 'Disconnected'} />
          {health?.version && (
            <SettingsRow label="API Uptime" value={`${Math.floor((health.version as Record<string, string>)?.api_uptime_seconds ? Number((health.version as Record<string, string>).api_uptime_seconds) / 60 : 0)} min`} />
          )}
          {roles.length > 0 && (
            <SettingsRow label="Platform Roles" value={`${roles.length} defined`} />
          )}
        </SettingsSection>
      </div>

      {/* Security notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
        <div className="flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-medium text-amber-700">Security Notes</p>
            <ul className="text-xs text-amber-600 mt-1 space-y-0.5">
              <li>&bull; eSanza uses passwordless authentication. No password change controls.</li>
              <li>&bull; Sensitive settings are managed via server environment variables.</li>
              <li>&bull; Platform settings here are read-only display. Edit via server config.</li>
              <li>&bull; Never expose API keys, SMTP passwords, Turnstile secrets, or database URLs.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

function SettingsSection({ icon, title, children, className = '' }: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <span className="text-slate-400">{icon}</span>
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      </div>
      <div className="divide-y divide-slate-50">{children}</div>
    </div>
  );
}

function SettingsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-700 font-medium">{value}</span>
    </div>
  );
}

export default PlatformSettingsPage;
